import { createApi } from "@reduxjs/toolkit/query/react";
import customBaseQuery from "../../../../utils/customBaseQuery";
import { gql } from "graphql-request";

export const LearningPathAuthoringApiSlice = createApi({
  reducerPath: "learningPathAuthoringApiSlice",
  baseQuery: customBaseQuery,
  refetchOnFocus: true,
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getLearningPath: builder.query({
      query: (learningPathId: string) => ({
        url: `/learning-path?id=${learningPathId}`,
        method: "GET"
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    createLearningPath: builder.mutation({
      query: (body): any => ({
        url: "/learning-path",
        method: "POST",
        body: body
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    updateLearningPath: builder.mutation({
      query: ({ learningPathId, body }): any => ({
        url: `/learning-path/${learningPathId}`,
        method: "POST",
        body: body
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    getGraphLearningPath: builder.query({
      query: (id): any => ({
        url: "/graphql",
        body: gql`
          query GetLearningPathById($id: String!) {
            getLearningPathById(id: $id) {
              data {
                id
                cataloged
                isCertificate
                org
                status
                duration
                category
                createdZoneId
                updatedZoneId
                createdby
                createdat
                updatedby
                updatedat
                templateId
                clonedId
                title
                socialInteractions {
                  contentId
                  contentType
                  interactionCriteria
                }
                shortDescription
                longDescription
                prerequisite
                learningObjective
                demonstrableOutcome
                searchTags {
                  defaultTag
                  contentCategory
                  contentSubCategory
                  contentUsage
                  contentRegion
                  contentDomain
                }
                skills
                difficultyLevel
                duration
                type
                access
                launchDate
                expiryDate
                associatedRoles
                stakeholder {
                  owner
                  reviewer
                  observer
                  publisher
                  learner
                }
                language
                publishedat
                publishedby
                sourceName
                sourceType
                branding {
                  promo
                  thumbnail
                  banner
                }
                sectionList {
                  id
                  org
                  createdZoneId
                  updatedZoneId
                  createdby
                  createdat
                  title
                  shortDescription
                  searchTags {
                    defaultTag
                  }
                  skills
                  learningPathId
                  status
                  locked
                  lockedType
                  accessEnabled
                  accessCriteria {
                    joblevel
                    progress
                    userAccess {
                      cohort
                      userId
                      userGroup
                    }
                  }
                  lockedSectionId
                  lockedContentId
                  contentList {
                    id
                    contentId
                    orderinsection
                    sourcename
                    sourcetype
                    joblevel
                    artifacttype
                    proficiency
                    publishedat
                    contentType
                    shortdescription
                    duration
                    difficultylevel
                    launchdate
                    title
                    logo
                    skills {
                      type
                      skillid
                      parentskill
                      preferredskillname {
                        en_UK
                        en_US
                      }
                    }
                    brandingurls {
                      promo
                      thumbnail
                      banner
                    }
                    branding {
                      promo
                      thumbnail
                      banner
                    }
                    updatedby
                    language
                    stakeholder {
                      owner
                      observer
                      nominationApprover
                      reviewer
                    }
                    expectedoutcome
                    longdescription
                  }
                  connectionList {
                    id
                    sourceSectionId
                    nextSectionId
                    title
                  }
                  connectionList {
                    id
                    sourceSectionId
                    nextSectionId
                    title
                  }
                }
              }
              error
            }
          }
        `,
        params: { variables: { id: id } }
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    addSection: builder.query({
      query: (body) => ({
        url: "/learning-path/section",
        method: "POST",
        body: body
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    updateSection: builder.mutation<any, { sectionId: string; body: any }>({
      query: ({ sectionId, body }) => ({
        url: `/learning-path/section/${sectionId}`,
        method: "POST",
        body: {
          title: "Section Title",
          shortDescription: "",
          searchTags: {
            defaultTag: []
          },
          skills: [],
          locked: false,
          lockedType: "free",
          accessEnabled: false,
          accessCriteria: {
            joblevel: [],
            progress: 0,
            userAccess: {
              cohort: [],
              userId: [],
              userGroup: []
            }
          },
          status: "draft",
          ...body
        }
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    addContent: builder.query({
      query: (body) => ({
        url: "/learning-path/content",
        method: "POST",
        body: body
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    deleteContent: builder.query({
      query: ({ contentId, body }) => ({
        url: `/learning-path/content/${contentId}`,
        method: "DELETE",
        body: {
          learningpathId: body.learningpathId,
          sectionId: body.sectionId
        }
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    deleteSection: builder.query({
      query: ({ id, body }) => ({
        url: `/learning-path/section/${id}`,
        method: "DELETE",
        body: body
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    updateContentStatus: builder.mutation({
      query: ({
        learningPathId,
        body
      }: {
        learningPathId: string;
        body: {
          status: "draft" | "inReview" | "published" | "deleted";
          remarks: string;
        };
      }) => ({
        url: `/learning-path/status/${learningPathId}`,
        method: "PUT",
        body
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    deleteLearningPath: builder.query({
      query: ({ id, remarks }: any) => ({
        url: `/learning-path/${id}`,
        method: "DELETE",
        body: {
          remarks: remarks
        }
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    rearrangeSection: builder.query({
      query: ({ lpId, body }) => ({
        url: `/learning-path/rearrange-section/${lpId}`,
        method: "POST",
        body: body
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    lpCloneById: builder.query({
      query: (lpId) => ({
        url: `/learning-path/cloneLearningPath/${lpId}`,
        method: "POST"
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    rearrangeContents: builder.query({
      query: ({ lpId, sectionId, body }) => ({
        url: `/learning-path/rearrange-content/${lpId}/${sectionId}`,
        method: "POST",
        body: body
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    validateLearningPath: builder.query({
      query: (id) => ({
        url: `/learning-path/validate/${id}`,
        method: "GET"
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    getContentInSection: builder.query({
      query: ({ contentId, sectionId }): any => ({
        url: "/graphql",
        body: gql`
          query GetContentInSection($contentId: String!, $sectionId: String!) {
            getContentInSection(contentId: $contentId, sectionId: $sectionId) {
              data {
                id
                contentId
                orderinsection
                sourcename
                sourcetype
                joblevel
                artifacttype
                proficiency
                contentType
                shortdescription
                duration
                difficultylevel
                launchdate
                title
                logo
                skills {
                  type
                  skillid
                  parentskill
                  preferredskillname {
                    en_UK
                    en_US
                  }
                }
                branding {
                  promo
                  thumbnail
                  banner
                }
                brandingurls {
                  promo
                  thumbnail
                  banner
                }
                updatedby
                language
                stakeholder {
                  owner
                  observer
                  nominationApprover
                  reviewer
                }
                expectedoutcome
                longdescription
              }
              error
            }
          }
        `,
        params: { variables: { contentId: contentId, sectionId: sectionId } }
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    getSection: builder.query({
      query: ({ sectionId, learningpathId }): any => ({
        url: "/graphql",
        body: gql`
          query GetSection($sectionId: String!, $learningpathId: String!) {
            getSection(sectionId: $sectionId, learningpathId: $learningpathId) {
              data {
                id
                org
                createdZoneId
                updatedZoneId
                createdby
                createdat
                title
                shortDescription
                searchTags {
                  defaultTag
                }
                skills
                learningPathId
                status
                locked
                lockedType
                accessEnabled
                accessCriteria {
                  joblevel
                  progress
                  userAccess {
                    cohort
                    userId
                    userGroup
                  }
                }
                lockedSectionId
                lockedContentId
                contentList {
                  id
                  contentId
                  orderinsection
                  sourcename
                  sourcetype
                  joblevel
                  artifacttype
                  proficiency
                  publishedat
                  contentType
                  shortdescription
                  duration
                  difficultylevel
                  launchdate
                  title
                  logo
                  skills {
                    type
                    skillid
                    parentskill
                    preferredskillname {
                      en_UK
                      en_US
                    }
                  }
                  brandingurls {
                    promo
                    thumbnail
                    banner
                  }
                  branding {
                    promo
                    thumbnail
                    banner
                  }
                  updatedby
                  language
                  stakeholder {
                    owner
                    observer
                    nominationApprover
                    reviewer
                  }
                  expectedoutcome
                  longdescription
                }
                connectionList {
                  id
                  sourceSectionId
                  nextSectionId
                  title
                }
                connectionList {
                  id
                  sourceSectionId
                  nextSectionId
                  title
                }
              }
              error
            }
          }
        `,
        params: {
          variables: { sectionId: sectionId, learningpathId: learningpathId }
        }
      }),
      extraOptions: { service: "LP_Authoring" }
    }),
    postPublishLPById: builder.mutation({
      query: ({ lpData, lpId }: { lpData: any; lpId: any }) => ({
        url: `/learning-path/post-publish/${lpId}`,
        method: "PUT",
        body: lpData
      }),
      extraOptions: { service: "LP_Authoring" }
    })
  })
});

export const {
  useGetLearningPathQuery,
  useLazyGetLearningPathQuery,
  useCreateLearningPathMutation,
  useUpdateLearningPathMutation,
  useGetGraphLearningPathQuery,
  useLazyGetGraphLearningPathQuery,
  useAddSectionQuery,
  useLazyAddSectionQuery,
  useLazyAddContentQuery,
  useUpdateSectionMutation,
  useDeleteContentQuery,
  useLazyDeleteContentQuery,
  useDeleteSectionQuery,
  useLazyDeleteSectionQuery,
  useUpdateContentStatusMutation,
  useDeleteLearningPathQuery,
  useLazyDeleteLearningPathQuery,
  useLazyRearrangeSectionQuery,
  useLazyRearrangeContentsQuery,
  useLazyValidateLearningPathQuery,
  useLazyGetContentInSectionQuery,
  useLazyGetSectionQuery,
  useLazyLpCloneByIdQuery,
  useLpCloneByIdQuery,
  usePostPublishLPByIdMutation
} = LearningPathAuthoringApiSlice;
